import { calculateNotaryFee } from "./notaryFees";

export enum ProjectType {
  KAUF = "KAUF",
  KAUF_GRUNDSCHULDABTRETUNG = "KAUF_GRUNDSCHULDABTRETUNG",
  KAUF_GRUNDSCHULDBESTELLUNG = "KAUF_GRUNDSCHULDBESTELLUNG",
  GRUNDSCHULDABTRETUNG = "GRUNDSCHULDABTRETUNG",
  GRUNDSCHULDBESTELLUNG = "GRUNDSCHULDBESTELLUNG",
  LOESCHUNG = "LOESCHUNG",
}

export enum ResponsibleParty {
  BANK = "BANK",
  EIGENTUEMER = "EIGENTUEMER",
  NOTAR = "NOTAR",
}

export interface NotaryFees {
  /** Vertragsvollzugsgebühren */
  contractExecutionFees: number;
  /** Beurkundung des Kaufvertrags */
  purchaseContractCertification: number;
  /** Beurkundung der Grundschuld / Hypothek */
  mortgageCertification: number;
  /** Notarielle Rangbescheinigung */
  rankCertificate: number;
  /** Beglaubigung von Unterschriften */
  signatureAuthentication: number;
  /** Vollzugstätigkeiten */
  otherProcessingActivities: number;
  /** Abwicklung via Notaranderkonto */
  notaryAccountProcessing: number;
}

export interface LandRegistryFees {
  /** Auflassungsvormerkung */
  transferReservation: number;
  /** Umschreibung Eigentümer */
  ownerTransfer: number;
  /** Veränderung von Grundbucheinträgen */
  landRegisterChanges: number;
  /** Eintragung Hypothek/Grundschuld */
  mortgageEntry: number;
  /** Löschung von Grundschuld / Hypothek */
  landRegisterDeletion: number;
}

export interface CalculationResult {
  notaryFees: NotaryFees;
  notarySubtotal: number;
  notaryVAT: number;
  totalNotaryFees: number;
  landRegistryFees: LandRegistryFees;
  totalLandRegistryFees: number;
  totalCosts: number;
}

export interface CalculationParams {
  projectType: ProjectType;
  purchasePrice?: number;
  mortgageAmount?: number;
  useNotaryAccount?: boolean;
  requireRankCertificate?: boolean;
  responsibleParty?: ResponsibleParty;
  bankHasDraft?: boolean;
}

const calculateSignatureAuthentication = (mortgageAmount: number) => {
  return roundMoney(Math.max(Math.min(0.2 * mortgageAmount, 70), 20));
};

export const calculateNotaryCosts = ({
  projectType,
  purchasePrice,
  mortgageAmount,
  useNotaryAccount = false,
  requireRankCertificate = false,
  responsibleParty = ResponsibleParty.NOTAR,
  bankHasDraft = false,
}: CalculationParams): CalculationResult => {
  const notaryFees: NotaryFees = {
    contractExecutionFees: 0,
    purchaseContractCertification: 0,
    mortgageCertification: 0,
    rankCertificate: 0,
    signatureAuthentication: 0,
    otherProcessingActivities: 0,
    notaryAccountProcessing: 0,
  };

  const landRegistryFees: LandRegistryFees = {
    transferReservation: 0,
    ownerTransfer: 0,
    landRegisterChanges: 0,
    mortgageEntry: 0,
    landRegisterDeletion: 0,
  };

  switch (projectType) {
    case ProjectType.KAUF:
      if (purchasePrice) {
        const baseFee = calculateNotaryFee(purchasePrice);
        notaryFees.purchaseContractCertification = baseFee * 2;
        notaryFees.contractExecutionFees = roundMoney(baseFee * 0.5);
        notaryFees.otherProcessingActivities = roundMoney(baseFee * 0.5);
        landRegistryFees.transferReservation = roundMoney(baseFee * 0.5);
        landRegistryFees.ownerTransfer = roundMoney(baseFee);

        if (useNotaryAccount) {
          notaryFees.notaryAccountProcessing = roundMoney(baseFee);
        }
      }
      break;

    case ProjectType.KAUF_GRUNDSCHULDABTRETUNG:
      if (purchasePrice && mortgageAmount) {
        const purchaseBaseFee = calculateNotaryFee(purchasePrice);
        const mortgageBaseFee = calculateNotaryFee(mortgageAmount);

        notaryFees.purchaseContractCertification = purchaseBaseFee * 2;
        notaryFees.contractExecutionFees = roundMoney(purchaseBaseFee * 0.5);
        notaryFees.otherProcessingActivities = roundMoney(
          purchaseBaseFee * 0.5,
        );
        notaryFees.mortgageCertification = mortgageBaseFee;
        notaryFees.signatureAuthentication =
          calculateSignatureAuthentication(mortgageBaseFee);
        landRegistryFees.transferReservation = roundMoney(
          purchaseBaseFee * 0.5,
        );
        landRegistryFees.ownerTransfer = roundMoney(purchaseBaseFee);
        landRegistryFees.landRegisterChanges = roundMoney(
          mortgageBaseFee * 0.5,
        );

        if (useNotaryAccount) {
          notaryFees.notaryAccountProcessing = roundMoney(purchaseBaseFee);
        }
      }
      break;

    case ProjectType.KAUF_GRUNDSCHULDBESTELLUNG:
      if (purchasePrice && mortgageAmount) {
        const purchaseBaseFee = calculateNotaryFee(purchasePrice);
        const mortgageBaseFee = calculateNotaryFee(mortgageAmount);

        notaryFees.purchaseContractCertification = purchaseBaseFee * 2;
        notaryFees.contractExecutionFees = roundMoney(purchaseBaseFee * 0.5);
        notaryFees.mortgageCertification = mortgageBaseFee;
        notaryFees.otherProcessingActivities = roundMoney(
          purchaseBaseFee * 0.5,
        );
        landRegistryFees.transferReservation = roundMoney(
          purchaseBaseFee * 0.5,
        );
        landRegistryFees.ownerTransfer = roundMoney(purchaseBaseFee);
        landRegistryFees.mortgageEntry = roundMoney(mortgageBaseFee);

        if (useNotaryAccount) {
          notaryFees.notaryAccountProcessing = roundMoney(purchaseBaseFee);
        }

        if (!useNotaryAccount && requireRankCertificate) {
          notaryFees.rankCertificate = roundMoney(0.3 * mortgageBaseFee);
        }
      }
      break;

    case ProjectType.GRUNDSCHULDBESTELLUNG:
      if (mortgageAmount) {
        const mortgageBaseFee = calculateNotaryFee(mortgageAmount);
        notaryFees.mortgageCertification = mortgageBaseFee;
        notaryFees.otherProcessingActivities = roundMoney(
          mortgageBaseFee * 0.5,
        );
        landRegistryFees.mortgageEntry = roundMoney(mortgageBaseFee);
      }
      break;

    case ProjectType.GRUNDSCHULDABTRETUNG:
      if (mortgageAmount) {
        const mortgageBaseFee = calculateNotaryFee(mortgageAmount);

        if (responsibleParty === ResponsibleParty.NOTAR) {
          notaryFees.mortgageCertification = mortgageBaseFee * 0.5;
        } else {
          notaryFees.signatureAuthentication =
            calculateSignatureAuthentication(mortgageBaseFee);
        }

        landRegistryFees.landRegisterChanges = roundMoney(
          mortgageBaseFee * 0.5,
        );
      }
      break;

    case ProjectType.LOESCHUNG:
      if (mortgageAmount) {
        const mortgageBaseFee = calculateNotaryFee(mortgageAmount);

        if (bankHasDraft) {
          notaryFees.signatureAuthentication =
            calculateSignatureAuthentication(mortgageBaseFee);
        } else {
          notaryFees.mortgageCertification = roundMoney(mortgageBaseFee * 0.5);
        }

        landRegistryFees.landRegisterDeletion = roundMoney(
          mortgageBaseFee * 0.5,
        );
      }
      break;
  }

  const notarySubtotal = Object.values(notaryFees).reduce(
    (sum, fee) => sum + fee,
    0,
  );
  const notaryVAT = roundMoney(notarySubtotal * 0.19);
  const totalNotaryFees = roundMoney(notarySubtotal + notaryVAT);

  const totalLandRegistryFees = roundMoney(
    Object.values(landRegistryFees).reduce((sum, fee) => sum + fee, 0),
  );

  const totalCosts = roundMoney(totalNotaryFees + totalLandRegistryFees);

  return {
    notaryFees,
    notarySubtotal,
    notaryVAT,
    totalNotaryFees,
    landRegistryFees,
    totalLandRegistryFees,
    totalCosts,
  };
};

const roundMoney = (value: number) => {
  return Math.round(value * 100) / 100;
};
